<template>
  <v-container class="grey-lighten-3 full-height">
    <v-row justify="center">
      <v-col cols="12" xl="10">
        <v-sheet class="report-paper pa-6" elevation="3" id="printable-area">

          <!-- Header (UNCHANGED) -->
          <div class="page-header">
            <div class="header-section d-flex justify-space-between align-start">
              <div class="company-info">
                <h4 class="company-name">
                  {{ order.branch?.company?.name }}
                </h4>
                <p class="company-text">
                  {{ order.branch?.company?.address }}
                </p>
                <p class="company-text">
                  <v-icon size="small">mdi-phone-outline</v-icon>
                  {{ order.branch?.company?.mobile }}
                </p>
                <p class="company-text">
                  <v-icon size="small">mdi-email-outline</v-icon>
                  {{ order.branch?.company?.email }}
                </p>
                <p v-if="order.branch?.company?.website" class="company-text">
                  <v-icon size="small">mdi-web</v-icon>
                  {{ order.branch?.company?.website }}
                </p>
              </div>

              <div class="logo-container">
                <v-img
                  :src="`${getFileServerUrl}/${order.branch?.company?.logo || 'uploads/demo_images/user.png'}`"
                  max-width="150"
                  max-height="70"
                  contain
                />
              </div>
            </div>

            <div class="text-center my-6">
              <h2 class="report-title">Textile Order Details</h2>
            </div>
          </div>

          <!-- Meta Info (DATA FIXED) -->
          <div class="meta-box">
            <div><strong>Buyer:</strong> {{ order?.buyer_order?.buyer?.name +' - '+order?.buyer_order?.buyer?.code }}</div>
            <div><strong>Dyeing House:</strong> {{ order?.dyeing_house?.name +' - '+order?.dyeing_house?.code }}</div>
            <div><strong>Code:</strong> {{ order.code }}</div>
            <div><strong>Status:</strong> {{ order.dyeing_status }}</div>
            <div>
              <strong>Start Date:</strong>
              {{ formatDateView(order.order_date) }}
            </div>
            <div>
              <strong>Delivery Date:</strong>
              {{ formatDateView(order.delivery_date) }}
            </div>
          </div>

          <!-- Details Table (UI SAME, DATA FIXED) -->
          <table class="dep-table order-table mt-4">
            <thead>
              <tr>
                <th>SL</th>
                <th>Style</th>
                <th>Color</th>
                <th class="text-right">Order Qty (PCS)</th>
                <th class="text-right">REQ Qty (KGs)</th>
                <th class="text-right">Yarn Count</th>
                <th class="text-right">Yarn Delivery</th>
                <th class="text-right">Yarn Receive</th>
                <th class="text-right">Receive Fabric Kniting</th>
                <th class="text-right">Dyeing Delivery</th>
                <th class="text-right">Dyeing Receive</th>
                
              </tr>
            </thead>

            <tbody>
              <tr v-for="(row, i) in order.breakdowns" :key="row.id">
                <!-- SL -->
                <td class="text-center">
                  {{ i + 1 }}
                </td>

                <!-- STYLE (ROWSPAN) -->
                <td >
                  {{ row?.style?.name || '-' }}
                  <br />
                  <small>({{ row?.style?.code || '-' }})</small>
                </td>

                <!-- COLOR (ROWSPAN) -->
                <td class="text-center" >
                  {{ row?.color?.name +' - '+row?.color?.code }}
                </td> 

                <td class="text-right">
                  {{ row.order_qty_pcs }}
                </td>

                <td class="text-right">
                  {{ row.req_qty_kg }}
                </td>

                <td class="text-right">
                  {{ row.yarn_count }}
                </td>
                <td class="text-right">
                  {{ row.yarn_delivery }}
                </td>
                <td class="text-right">
                  {{ row.yarn_receive }}
                </td>

                <td class="text-right">
                  {{ row.rec_fabric_knit }}
                </td>

                <td class="text-right">
                  {{ row.dye_delivery }}
                </td>

                <td class="text-right">
                  {{ row.dye_receive }}
                </td>

                
              </tr>

              <!-- TOTAL ROW -->
              <tr class="font-weight-bold">
                <td colspan="8" class="text-right">Total</td>
                <td class="text-right">{{ totalOrderQty }}</td>
                <td class="text-right">-</td>
                <td class="text-right">{{ totalDyeQty }}</td>
              </tr>
            </tbody>
          </table>

        </v-sheet>
      </v-col>
    </v-row>

    <!-- Print Button (UNCHANGED) -->
    <v-row justify="center" class="d-print-none mt-4">
      <v-btn color="primary" prepend-icon="mdi-printer" @click="print">
        Print
      </v-btn>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { formatDateView, formatDateDash } from "@/plugins/helpers";

const store = useStore();
const route = useRoute();

const order = ref({
  branch: {},
  breakdowns: [],
  style: {},
  color: {}
});

const getFileServerUrl = computed(
  () => store.getters.getFileServerUrl
);

/* TOTALS (DATA ONLY) */
const totalOrderQty = computed(() =>
  order.value.breakdowns.reduce(
    (sum, r) => sum + Number(r.order_qty_pcs || 0),
    0
  )
);

const totalDyeQty = computed(() =>
  order.value.breakdowns.reduce(
    (sum, r) => sum + Number(r.dye_qty_kg || 0),
    0
  )
);

async function fetchDetails(id) {
  const res = await store.dispatch("getDyeingOrderById", { id });
  order.value = res.data.data;
}

function print() {
  const printContent =
    document.getElementById("printable-area").innerHTML;

  const printWindow = window.open(
    "",
    "_blank",
    `width=${screen.width}, height=${screen.height}`
  );

  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>Finishing Report</title>
      <link href="https://cdn.jsdelivr.net/npm/@mdi/font@7.4.47/css/materialdesignicons.min.css" rel="stylesheet">
      <style>
        body { font-family: 'Times New Roman', serif; margin:0; padding:15px; }
        .dep-table { width:100%; border-collapse:collapse; font-size:13px; }
        .dep-table th, .dep-table td { border:1px solid #000; padding:6px; }
        .dep-table th { background:#e0e0e0; }
        .text-center { text-align:center; }
        .text-right { text-align:right; }
      </style>
    </head>
    <body>
      ${printContent}
    </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => printWindow.print(), 500);
}

onMounted(() => fetchDetails(route.params.id));
</script>

<style scoped>
.full-height { min-height: 100vh; }
.grey-lighten-3 { background-color: #eeeeee !important; }
.report-paper { background:white; }
.company-name { font-size:20px; font-weight:bold; text-transform:uppercase; }
.company-text { font-size:14px; }
.report-title { font-size:20px; text-align:center; margin:16px 0; }
.meta-box { border:1px solid #000; padding:8px; font-size:13px; margin-bottom:16px; }
.dep-table { width:100%; border-collapse:collapse; font-size:13px; }
.dep-table th, .dep-table td { border:1px solid #000; padding:6px; }
.dep-table th { background:#e0e0e0; }
.text-center { text-align:center; }
.text-right { text-align:right; }
</style>
